import { useState } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { updateTenderOpeningStatus } from './useUpdateTenderOpeningStatus';
import { buildTenderOpeningDownloadFilename } from '../utils/tenderOpeningHelpers';

const getFilenameFromContentDisposition = contentDisposition => {
  if (!contentDisposition?.includes('filename=')) return null;
  return contentDisposition.split('filename=')[1].replace(/"/g, '').trim();
};

const useInvitePanTender = ({
  procId,
  procurement,
  documents = [],
  onRefreshProcurement,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState({
    word: false,
    pdf: false,
  });
  const [searchText, setSearchText] = useState('');

  const sendInvitation = async (invitationData = {}) => {
    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return false;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        eventDate: invitationData.eventDate || null,
        startTime: invitationData.startTime || null,
        endTime: invitationData.endTime || null,
        tillEnd:
          invitationData.tillEnd !== undefined ? invitationData.tillEnd : true,
        location: invitationData.location || null,
        agenda: invitationData.agenda || null,
      };

      await axios.put(
        `${api.API_URL}/procurement/send-invitation-panitia-tender/${procId}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await updateTenderOpeningStatus(procId, 'isInvitePanitiaTenderCompleted');

      notify.success('Invitation to Panitia Tender sent successfully.');
      onRefreshProcurement?.();
      return true;
    } catch (err) {
      notify.apiError(err, 'send invitation to Panitia Tender');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveAsDraft = async (invitationData = {}) => {
    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return false;
    }

    setIsSavingDraft(true);
    try {
      const payload = {
        eventDate: invitationData.eventDate || null,
        startTime: invitationData.startTime || null,
        endTime: invitationData.endTime || null,
        tillEnd:
          invitationData.tillEnd !== undefined ? invitationData.tillEnd : true,
        location: invitationData.location || null,
        agenda: invitationData.agenda || null,
      };

      await axios.put(
        `${api.API_URL}/procurement/save-as-draft-invitation-panitia-tender/${procId}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      notify.success('Draft saved successfully.');
      onRefreshProcurement?.();
      return true;
    } catch (err) {
      notify.apiError(err, 'save invitation draft');
      return false;
    } finally {
      setIsSavingDraft(false);
    }
  };

  const generateDocument = async type => {
    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return false;
    }
    if (!procId) {
      notify.error(
        'Procurement ID is missing. Refresh the page and try again.'
      );
      return false;
    }

    const endpoint =
      type === 'word'
        ? `${api.API_URL}/procurement/GenerateDocumentInvitePanitiaTenderWord?procurementId=${procId}`
        : `${api.API_URL}/procurement/GenerateDocumentInvitePanitiaTenderPDF?procurementId=${procId}`;

    const defaultFilename = buildTenderOpeningDownloadFilename({
      procurementNumber: procurement?.procurementNumber,
      title: 'Invite_Panitia_Tender',
      submittedDate: procurement?.submittedDateTenderOpening,
      extension: type === 'word' ? 'docx' : 'pdf',
    });

    setIsGeneratingDocument(prev => ({ ...prev, [type]: true }));
    try {
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `Request failed with status code ${response.status}`;
        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch {
          // response body bukan JSON
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const filename =
        getFilenameFromContentDisposition(
          response.headers.get('Content-Disposition')
        ) || defaultFilename;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      notify.apiError(error, `generate ${type.toUpperCase()} document`);
      return false;
    } finally {
      setIsGeneratingDocument(prev => ({ ...prev, [type]: false }));
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const term = searchText.trim().toLowerCase();
    if (!term) return true;
    const name = (doc.documentName || doc.customFileName || '').toLowerCase();
    return name.includes(term);
  });

  return {
    documents: filteredDocuments,
    isSubmitting,
    isSavingDraft,
    isGeneratingDocument,
    searchText,
    setSearchText,
    sendInvitation,
    saveAsDraft,
    generateDocument,
  };
};

export default useInvitePanTender;
